import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { accountService } from '../../services/api/accountService';
import LoadingOverlay from '../common/LoadingOverlay';
import AuthCard from '../common/AuthCard/AuthCard';
import {
  BodyText,
  Field,
  FieldLabel,
  Form,
  PasswordWrap,
  RuleItem,
  RuleList,
  StatusBadge,
  StatusMessage,
  SubmitButton,
  TextInput,
  TextLink,
} from '../common/AuthCard/AuthCard.styled';

const REQUIREMENT_ROWS = [
  { key: 'length', text: '8–50 characters' },
  { key: 'uppercase', text: 'One uppercase letter' },
  { key: 'lowercase', text: 'One lowercase letter' },
  { key: 'digit', text: 'One number' },
  { key: 'special', text: 'One special character' },
  { key: 'match', text: 'Passwords match' },
];

const signInFooter = (
  <p>
    Remember your password? <TextLink to="/login">Sign in</TextLink>
  </p>
);

export const ResetPasswordForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('code');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** `done` = password changed; `expired` = link no longer works. */
  const [outcome, setOutcome] = useState(null);

  const passwordRules = {
    length: newPassword.length >= 8 && newPassword.length <= 50,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /\d/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0,
  };

  const isFormValid = Object.values(passwordRules).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      setError('Please meet all password requirements before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await accountService.resetPassword({
        Token: token,
        NewPassword: newPassword,
      });

      switch (response.status) {
        case 201:
        case 204:
          setOutcome('done');
          break;
        case 401:
        case 404:
          setOutcome('expired');
          break;
        case 409:
          setError('Please choose a different password than your previous one.');
          break;
        case 500:
        default:
          setError('Oops! Something went wrong. Please try again later.');
          break;
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Unexpected error occurred. Try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (outcome === 'done') {
    return (
      <AuthCard
        title="Password updated"
        badge={
          <StatusBadge $tone="success" aria-hidden>
            <CheckCircleRoundedIcon />
          </StatusBadge>
        }
      >
        <BodyText>Your password has been reset. You can now sign in with it.</BodyText>
        <SubmitButton type="button" onClick={() => navigate('/login')}>
          Sign in
        </SubmitButton>
      </AuthCard>
    );
  }

  if (!token || outcome === 'expired') {
    return (
      <AuthCard
        title={token ? 'This link has expired' : 'This link is not valid'}
        footer={signInFooter}
        badge={
          <StatusBadge $tone="error" aria-hidden>
            <LinkOffRoundedIcon />
          </StatusBadge>
        }
      >
        <BodyText>
          {token
            ? 'Reset links work for 15 minutes. Request a new one and use it right away.'
            : 'This reset link is missing or incomplete. Open the link from your email again, or request a new one.'}
        </BodyText>
        <SubmitButton type="button" onClick={() => navigate('/forgot-password')}>
          Request a new reset link
        </SubmitButton>
      </AuthCard>
    );
  }

  const passwordField = ({ id, label, value, onChange, shown, onToggle, toggleLabel }) => (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <PasswordWrap>
        <TextInput
          id={id}
          type={shown ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          maxLength={50}
          required
          disabled={isSubmitting}
        />
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={shown ? `Hide ${toggleLabel}` : `Show ${toggleLabel}`}
        >
          {shown ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </PasswordWrap>
    </Field>
  );

  return (
    <>
      <AuthCard
        title="Choose a new password"
        lead="Pick a strong password you haven't used before on this account."
        footer={signInFooter}
      >
        <Form onSubmit={handleSubmit} noValidate>
          {passwordField({
            id: 'reset-password-new',
            label: 'New password',
            value: newPassword,
            onChange: setNewPassword,
            shown: showNewPassword,
            onToggle: () => setShowNewPassword((prev) => !prev),
            toggleLabel: 'new password',
          })}
          {passwordField({
            id: 'reset-password-confirm',
            label: 'Confirm new password',
            value: confirmPassword,
            onChange: setConfirmPassword,
            shown: showConfirmPassword,
            onToggle: () => setShowConfirmPassword((prev) => !prev),
            toggleLabel: 'confirm password',
          })}

          <RuleList aria-label="Password requirements">
            {REQUIREMENT_ROWS.map(({ key, text }) => (
              <RuleItem key={key} $met={passwordRules[key]}>
                {passwordRules[key] ? (
                  <CheckCircleRoundedIcon aria-hidden />
                ) : (
                  <RadioButtonUncheckedRoundedIcon aria-hidden />
                )}
                {text}
              </RuleItem>
            ))}
          </RuleList>

          {error && (
            <StatusMessage $variant="error" role="alert">
              {error}
            </StatusMessage>
          )}

          <SubmitButton type="submit" disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? 'Saving…' : 'Reset password'}
          </SubmitButton>
        </Form>
      </AuthCard>
      {isSubmitting && <LoadingOverlay />}
    </>
  );
};
